import React, { useEffect, useState } from "react";
import "./BugReport.style.scss";
import { jwtDecode } from "jwt-decode";
import { sendBugReportPHP, sendPlayerReportPHP } from "../../../utils/functions.ts";
import { getTodayEasternYMD } from "../../../utils/dateUtils.ts";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";

const BUG_ENDPOINT = "https://www.fbxchange.com/ReactPHP/bugReport.php";

const BugReport = () => {
    const { updateActiveSideButton } = useNavBar();
    const [userData, updateUserData] = useState(null);
    const [adminMode, setAdminMode] = useState(false);
    const [bugReports, setBugReports] = useState([]);
    const [bugLoading, setBugLoading] = useState(false);
    const [bugMessage, setBugMessage] = useState("");
    const [bugNotes, setBugNotes] = useState({});
    const [formData, setFormData] = useState({
        operating: "-",
        browser: "-",
        section: "-",
        bugDescription: "",
        correctFirst: "",
        correctLast: "",
        correctSchool : "",
        correctPosition : "",
        correctInformation : ""
    });
    const [reverse, setReverse] = useState(false);

    const isAdmin = String(userData?.userContactLevel ?? userData?.security ?? "") === "1";

    useEffect(() => {
        if (!userData) {
            const token = localStorage.getItem("user2026");
            if (token) {
                const theU = jwtDecode(token);
                updateUserData(theU);
            } else {
                console.warn("No token found in localStorage");
            }
        }
    }, [userData]);

    const postBugAction = async (payload) => {
        const response = await fetch(BUG_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams(payload).toString(),
        });

        return response.json().catch(() => ({}));
    };

    const loadBugReports = async () => {
        if (!isAdmin) return;

        setBugLoading(true);
        setBugMessage("");

        try {
            const data = await postBugAction({ script: "getBug" });
            const reports = Array.isArray(data) ? data : [];
            setBugReports(reports);
            setBugNotes(
                reports.reduce((notes, report) => {
                    notes[report.ID] = report.danNote || "";
                    return notes;
                }, {})
            );
        } catch (error) {
            setBugMessage("Could not load bug reports.");
            setBugReports([]);
        } finally {
            setBugLoading(false);
        }
    };

    useEffect(() => {
        if (adminMode && isAdmin) {
            loadBugReports();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminMode, isAdmin]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const sendBugReport = () => {
        const dataToSend = {
            ...formData,
            displayName: userData?.displayName || "Anonymous",
            team: userData?.team || "Unknown",
            date: getTodayEasternYMD(),
        };
        sendBugReportPHP(dataToSend);
        if (isAdmin) loadBugReports();
        updateActiveSideButton(null);
    };

    const sendPlayerReport = () =>{
        const dataToSend = {
            ...formData,
            displayName: userData?.displayName || "Anonymous",
            team: userData?.team || "Unknown",
            date: getTodayEasternYMD(),
        };
        sendPlayerReportPHP(dataToSend);
        if (isAdmin) loadBugReports();
        updateActiveSideButton(null);
    }

    const setView = (inval) => {
        setAdminMode(false);
        setReverse(inval);
    }

    const setAdminView = () => {
        setAdminMode(true);
    };

    const markBug = async (ID, script) => {
        if (!ID) return;

        setBugMessage("");
        try {
            const data = await postBugAction({ script, ID });
            if (data?.success === false) {
                setBugMessage("Could not update report.");
                return;
            }
            await loadBugReports();
        } catch (error) {
            setBugMessage("Could not update report.");
        }
    };

    const saveBugNote = async (ID) => {
        if (!ID) return;

        setBugMessage("");
        try {
            const data = await postBugAction({
                script: "setNoteBug",
                ID,
                danNote: bugNotes[ID] || "",
            });
            if (data?.success === false) {
                setBugMessage("Could not save note.");
                return;
            }
            await loadBugReports();
        } catch (error) {
            setBugMessage("Could not save note.");
        }
    };

    const deleteBug = async (ID) => {
        if (!ID) return;
        const confirmed = window.confirm("Delete this bug report permanently?");
        if (!confirmed) return;

        setBugMessage("");
        try {
            const data = await postBugAction({ script: "deleteBug", ID });
            if (data?.success === false) {
                setBugMessage("Could not delete report.");
                return;
            }
            await loadBugReports();
        } catch (error) {
            setBugMessage("Could not delete report.");
        }
    };

    const cleanValue = (value) => {
        if (value === undefined || value === null || value === "") return "-";
        return String(value);
    };

    const getReportText = (report) => {
        const playerText = [
            report.playerName,
            report.playerSchool,
            report.playerPosition,
            report.playerInformation,
        ]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .join(" | ");

        return playerText || report.message || "-";
    };

    return (
        <div className="bugBox">
            <div className="row alert alert-success p-3 text-center">
                <div className="fw-bold">Thank you for reporting an error with FBX</div>
                <div className="text-center mt-1">Please try to give as much information as you can in the Error.</div>
                <div className="text-center mt-1 fw-bold">For Player Errors, Please use the Report Player Error</div>
            </div>

            <div className="col-44 offset-2 gap-2 d-flex justify-content-between mt-2 bug-report-tabs" style={{ lineHeight: "35px" }}>
                <div className={`btn ${isAdmin ? 'flex-fill' : 'col-24'} ${!adminMode && !reverse ? 'btn-navy' : 'btn-outline-navy'}`} onClick={() => setView(false)}>Error Report</div>
                <div className={`btn ${isAdmin ? 'flex-fill' : 'col-24'} ${!adminMode && reverse ? 'btn-navy' : 'btn-outline-navy'}`} onClick={() => setView(true)}>Player Error</div>
                {isAdmin && (
                    <div className={`btn flex-fill ${adminMode ? 'btn-navy' : 'btn-outline-navy'}`} onClick={setAdminView}>Admin Reports</div>
                )}
            </div>
            {adminMode && isAdmin ? (
                <div className="mt-3 bug-admin-panel">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-bold text-navy">Bug Reports</div>
                        <button type="button" className="btn btn-sm btn-outline-navy" onClick={loadBugReports} disabled={bugLoading}>
                            {bugLoading ? "Loading..." : "Refresh"}
                        </button>
                    </div>

                    {bugMessage && <div className="alert alert-warning py-2 fs-7">{bugMessage}</div>}

                    {bugLoading ? (
                        <div className="text-muted">Loading bug reports...</div>
                    ) : bugReports.length === 0 ? (
                        <div className="text-muted">No bug reports found.</div>
                    ) : (
                        <div className="bug-admin-list">
                            {bugReports.map((report) => {
                                const isRead = String(report.isRead || "").toLowerCase() === "yes";
                                const isResolved = String(report.isFixed || "").toLowerCase() === "yes";
                                const isContacted = String(report.isContact || "").toLowerCase() === "yes";

                                return (
                                    <div className="bug-admin-row" key={report.ID}>
                                        <div className="d-flex justify-content-between gap-2">
                                            <div>
                                                <div className="fw-bold">
                                                    #{report.ID} {cleanValue(report.displayName)} [{cleanValue(report.team)}]
                                                </div>
                                                <div className="text-muted">{cleanValue(report.timeStamp)}</div>
                                            </div>
                                            <div className="text-end bug-admin-status">
                                                <span className={`badge ${isRead ? "bg-success" : "bg-secondary"} me-1`}>
                                                    {isRead ? "Read" : "Unread"}
                                                </span>
                                                <span className={`badge ${isResolved ? "bg-success" : "bg-warning"} me-1`}>
                                                    {isResolved ? "Resolved" : "Open"}
                                                </span>
                                                <span className={`badge ${isContacted ? "bg-success" : "bg-secondary"}`}>
                                                    {isContacted ? "Contacted" : "No Contact"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-2">
                                            <div className="small text-muted">
                                                {cleanValue(report.sections)} | {cleanValue(report.browser)} | {cleanValue(report.os)}
                                            </div>
                                            <div className="bug-admin-message">{getReportText(report)}</div>
                                            {(report.phone || report.email) && (
                                                <div className="small text-muted">
                                                    {cleanValue(report.phone)} | {cleanValue(report.email)}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-2">
                                            <textarea
                                                className="form-control form-control-sm"
                                                rows="2"
                                                value={bugNotes[report.ID] || ""}
                                                onChange={(event) =>
                                                    setBugNotes((current) => ({
                                                        ...current,
                                                        [report.ID]: event.target.value,
                                                    }))
                                                }
                                                placeholder="Admin note"
                                            />
                                        </div>

                                        <div className="d-flex gap-1 mt-2 flex-wrap">
                                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => markBug(report.ID, "markRead")} disabled={isRead}>
                                                Mark Read
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-success" onClick={() => markBug(report.ID, "markResolved")} disabled={isResolved}>
                                                Mark Resolved
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-info" onClick={() => markBug(report.ID, "markContact")} disabled={isContacted}>
                                                Mark Contacted
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-navy" onClick={() => saveBugNote(report.ID)}>
                                                Save Note
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => deleteBug(report.ID)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : !reverse ? (
                <div id="bugPart">
                    <div className="mt-4">
                        {/* Operating System Dropdown */}
                        <div className="d-flex align-items-center gap-2">
                            <div className="col-18 text-end">What Operating System are you using?</div>
                            <div className="col-28 text-start">
                                <select name="operating" className="form-control" value={formData.operating} onChange={handleInputChange}>
                                    <option value="-">Operating System</option>
                                    <option value="windows">Windows</option>
                                    <option value="macos">Mac OS</option>
                                    <option value="android_ios">Android IOS</option>
                                    <option value="apple_ios">Apple IOS</option>
                                </select>
                            </div>
                        </div>


                        {/* Browser Dropdown */}
                        <div className="d-flex align-items-center mt-1 gap-2">
                            <div className="col-18 text-end px-2">What Browser are you using?</div>
                            <div className="col-28 text-start">
                                <select name="browser" className="form-control" value={formData.browser} onChange={handleInputChange}>
                                    <option value="-">Browser</option>
                                    <option value="chrome">Chrome</option>
                                    <option value="firefox">Firefox</option>
                                    <option value="safari">Safari</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* Application Section Dropdown */}
                        <div className="d-flex align-items-center mt-1 gap-2">
                            <div className="col-18 text-end px-2">What Section of our Application is the Bug?</div>
                            <div className="col-28 text-start">
                                <select name="section" className="form-control" value={formData.section} onChange={handleInputChange}>
                                    <option value="-">Section</option>
                                    <option value="account">Account</option>
                                    <option value="Bug">Bug Report</option>
                                    <option value="legend">Legend</option>
                                    <option value="Fuzzt">Fuzzy Search</option>
                                    <option value="s1_search">S1 Search</option>
                                    <option value="s2_search">S2 Search</option>
                                    <option value="s3_search">S3 Search</option>
                                    <option value="l1_search">L1 Search</option>
                                    <option value="l2_search">L2 Search</option>
                                    <option value="overview_button">Overview Button</option>
                                    <option value="depth_chart">Depth Chart</option>
                                    <option value="export">Export</option>
                                    <option value="hard_data">Hard Data</option>
                                    <option value="transactions">Transactions</option>
                                    <option value="file_upload">File Upload</option>
                                    <option value="notes">Notes</option>
                                    <option value="reports">Reports</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Bug Description */}
                    <div className="py-3 text-center px-5 text-primary fw-bold">
                        Please give as much detail as possible, including any data you might have entered.
                    </div>
                    <div className="col-44 offset-2 mt-2">
                        <textarea name="bugDescription" className="form-control" rows="5" value={formData.bugDescription} onChange={handleInputChange}></textarea>
                    </div>
                    {/* Submit Button */}
                    <div className="mt-3 text-center">
                        <button className="btn btn-success col-48 text-white" onClick={sendBugReport}>
                            Send Error Report
                        </button>
                    </div>

                    {/* Display User Info */}
                    {userData?.displayName && (
                        <div className="mt-2 text-primary fw-bold text-center">
                            Sent By : {userData.displayName} [{userData.team}] [{getTodayEasternYMD()}]
                        </div>
                    )}
                </div>
            ) : (
                <div className="mt-4">
                    <div className="d-flex align-items-center gap-2">
                        <div className="col-18 text-end">Player First Name</div>
                        <div className="col-28 text-start">
                            <input className="form-control"  name="correctFirst"  placeholder="First Name" type="text" onChange={handleInputChange} value={formData.correctFirst} />
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="col-18 text-end">Player Last Name</div>
                        <div className="col-28 text-start">
                            <input className="form-control"  name="correctLast" placeholder="Last Name" type="text" onChange={handleInputChange}  value={formData.correctLast} />
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="col-18 text-end">University</div>
                        <div className="col-28 text-start">
                            <input className="form-control"  name="correctSchool"  placeholder="School" type="text" onChange={handleInputChange}  value={formData.correctSchool}/>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="col-18 text-end">Position</div>
                        <div className="col-28 text-start">
                            <input className="form-control"  name="correctPosition"  placeholder="Position" type="text" onChange={handleInputChange}  value={formData.correctPosition}/>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="col-18 text-end">Use this Space for Correction</div>
                        <div className="col-28 text-start">
                            <textarea  rows="5" name="correctInformation"  className="form-control" onChange={handleInputChange}  value={formData.correctInformation }></textarea>
                        </div>
                    </div>
                    <div className="mt-3 text-center">
                        <button className="btn btn-primary col-48 text-white" onClick={sendPlayerReport}>
                            Send Player Error
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BugReport;
